<script lang="ts">
  import { cleanFindingsText } from '../../lib/agentTransformations';
  // Shows the AI approver's assessment of a cashback process: a recommendation
  // (APPROVABLE / UNSURE / NOT_APPROVABLE) plus the agent's free-text findings. Purely
  // informational - it never changes the process; a human reviewer reads it and decides.
  // Fed by the /purchasepaybacks/{id}/verify endpoint (verify-receipt.controller.ts).
  export let open: boolean = false;
  export let loading: boolean = false;
  export let processId: string | number | null = null;
  export let decision: 'APPROVABLE' | 'UNSURE' | 'NOT_APPROVABLE' | 'APPROVED' | 'NOT APPROVED' | null = null;
  export let findings: string = '';
  export let errorMessage: string = '';

  const decisionLabel: Record<string, string> = {
    APPROVABLE: 'Agent is confident this process can be approved',
    APPROVED: 'Agent is confident this process can be approved',
    UNSURE: 'Agent is unsure whether this process can be approved',
    NOT_APPROVABLE: 'Agent thinks this process can NOT be approved',
    'NOT APPROVED': 'Agent thinks this process can NOT be approved',
  };

  const decisionClass: Record<string, string> = {
    APPROVABLE: 'bg-green-50 text-green-800 border-green-200',
    APPROVED: 'bg-green-50 text-green-800 border-green-200',
    UNSURE: 'bg-amber-50 text-amber-800 border-amber-200',
    NOT_APPROVABLE: 'bg-red-50 text-red-800 border-red-200',
    'NOT APPROVED': 'bg-red-50 text-red-800 border-red-200',
  };

  function handleClose() {
    open = false;
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (open && event.key === 'Escape') {
      handleClose();
    }
  }
</script>

<svelte:window on:keydown={handleKeyDown} />

{#if open}
  <div
    class="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-500/75"
  >
    <div
      class="relative flex max-h-[85vh] w-full max-w-2xl transform flex-col rounded bg-white shadow-xl transition-all"
    >
      <div class="flex items-center justify-between border-b px-6 py-4">
        <h3 class="text-lg font-semibold text-slate-900">
          AI verification{processId != null ? ` - process #${processId}` : ''}
        </h3>
        <button type="button" class="close-btn" on:click={handleClose}>✕</button
        >
      </div>

      <div class="flex flex-col gap-4 overflow-y-auto px-6 py-4">
        {#if loading}
          <div class="flex items-center gap-3 py-8">
            <div
              class="h-6 w-6 animate-spin rounded-full border-b-2 border-primary-600"
            ></div>
            <span class="text-sm text-slate-600">Running AI verification…</span>
          </div>
        {:else if errorMessage}
          <div
            class="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            {errorMessage}
          </div>
        {:else}
          {#if decision}
            <div
              class="rounded border px-4 py-3 text-sm font-semibold {decisionClass[
                String(decision).toUpperCase()
              ] ?? 'border-slate-200 bg-slate-50 text-slate-800'}"
            >
              {decisionLabel[String(decision).toUpperCase()] ?? decision}
            </div>
          {/if}
          <div>
            <div class="mb-1 text-xs font-semibold uppercase text-slate-500">
              Findings
            </div>
            <textarea
              readonly
              class="h-72 w-full rounded border border-gray-300 p-3 font-mono text-xs"
              >{cleanFindingsText(findings)}</textarea
            >
          </div>
        {/if}
      </div>

      <div class="flex justify-end border-t bg-slate-50 px-6 py-3">
        <button
          type="button"
          class="inline-flex items-center justify-center rounded border border-transparent bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 active:shadow-none"
          on:click={handleClose}
        >
          Close
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .close-btn {
    background: none;
    border: none;
    font-size: 20px;
    color: #6b7280;
    cursor: pointer;
    transition: color 0.2s;
  }
  .close-btn:hover {
    color: #111827;
  }
</style>
